import { Component, Input, inject } from '@angular/core';
import { OutcomeProgressModel, OutcomeTargetModel, ProjectOutcomeModel } from '../../../../../hub_schema/hubTypes';
import { MatDialog } from '@angular/material/dialog';
import { HubRecordEditorBase } from '../../_hub_record_editor_base';
import { AddEditProjectOutcomeTargetComponent } from './add-edit-project-outcome-target/add-edit-project-outcome-target.component';
import { AddEditOutcomeProgressDialogComponent } from './add-edit-outcome-progress-dialog/add-edit-outcome-progress-dialog.component';
import { take } from 'rxjs';

enum tabs {
    outcomeDetails,
    linkedStrategyOutcomes
}

@Component({
    selector: 'app-outcome-details',
    templateUrl: './outcome-details.component.html',
    styleUrls: ['./outcome-details.component.scss'],
    standalone: false
})
export class OutcomeDetailsComponent extends HubRecordEditorBase {
    public tabs = tabs;
    activeTab: tabs = tabs.outcomeDetails;

    private dialogService: MatDialog = inject(MatDialog);

    @Input()
    public projectOutcome: ProjectOutcomeModel;

    private markDirty(): void {
        this.projectEditForm!.controls.projectOutcomes.markAsDirty();
    }

    public setTabTo(tab: tabs, $e): void {
        this.activeTab = tab;
        $e.preventDefault();
    }

    // #region Targets

    public openAddEditTargetDialog(target: OutcomeTargetModel | null): void {
        const dialogRef = this.dialogService.open(AddEditProjectOutcomeTargetComponent, {
            width: '800px',
            maxWidth: 'none',
            data: {
                existingTarget: target,
                projectOutcome: this.projectOutcome,
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((savedTarget: OutcomeTargetModel) => {
            if (savedTarget) {
                if (!target) {
                    this.projectOutcome.targets!.push(savedTarget);
                    this.projectOutcome.targets!.sort((a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime());
                } else {
                    const index = this.projectOutcome.targets!.findIndex(t => t === target);
                    this.projectOutcome.targets![index] = savedTarget;
                }
                this.markDirty();
            }
        });
    }

    public deleteTarget(target: OutcomeTargetModel): void {
        const index = this.projectOutcome.targets!.findIndex(t => t === target);
        this.projectOutcome.targets!.splice(index, 1);
        this.markDirty();
    }

    // #endregion

    // #region Progress

    public selectedOutcomeHasStart(): boolean {
        return this.projectOutcome.progress?.some(p => p.isStart) ?? false;
    }

    public openAddEditProgressDialog(progress: OutcomeProgressModel | null, isStart: boolean): void {
        const dialogRef = this.dialogService.open(AddEditOutcomeProgressDialogComponent, {
            width: '800px',
            maxWidth: 'none',
            maxHeight: '80%',
            data: {
                projectOutcome: this.projectOutcome,
                existingProgress: progress,
                isStart: isStart
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((savedProgress: OutcomeProgressModel) => {
            if (savedProgress) {
                if (!this.projectOutcome.progress) {
                    this.projectOutcome.progress = [];
                }
                if (!progress) {
                    this.projectOutcome.progress.push(savedProgress);
                    this.projectOutcome.progress.sort((a, b) => new Date(a.progressDate).getTime() - new Date(b.progressDate).getTime());
                } else {
                    const index = this.projectOutcome.progress.findIndex(p => p === progress);
                    this.projectOutcome.progress[index] = savedProgress;
                }
                this.markDirty();
            }
        });
    }

    public deleteProgress(progress: OutcomeProgressModel): void {
        const index = this.projectOutcome.progress!.findIndex(p => p === progress);
        this.projectOutcome.progress!.splice(index, 1);
        this.markDirty();
    }

    // #endregion

    public doDetailsExist(): boolean {
        return !!(this.projectOutcome.targets?.length || this.projectOutcome.progress?.length);
    }
}
